import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Send, AlertCircle, CheckCircle2 } from "lucide-react";
import { TopBar } from "../components/TopBar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import BiField from "../components/case/BiField";
import { FileSlot, OtherDocuments } from "../components/case/FileUploaders";
import LifecycleBanner from "../components/case/LifecycleBanner";
import StatusThread from "../components/case/StatusThread";
import { formatCaseNumber } from "../lib/caseFormat";
import CaseExportButtons from "../components/case/CaseExportButtons";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";

const EMPTY_FORM = {
  applicant: {
    name: "",
    chinese_name: "",
    tel: "",
    email: "",
    mailing_address: "",
    mother_name: "",
    marital_status: "",
  },
  spouse: {
    name: "",
    contact: "",
    occupation: "",
    company_name: "",
    salary_per_month: "",
  },
  health: null,
  emergency_contact: {
    name: "",
    tel: "",
    resident_address: "",
    relationship: "",
  },
  files: {
    id_card: null,
    sales_form: null,
    ctos_report: null,
    photo: null,
    land_title: null,
    customer_payment_slip: null,
    other_documents: [],
  },
};

const formatTime = (d) => {
  if (!d) return "";
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const errorFor = (errors, field) => errors?.[field];

const Section = ({ title, children, testId }) => (
  <section
    className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 sm:p-8 space-y-6"
    data-testid={testId}
  >
    <h3 className="font-display text-xl tracking-tight font-medium text-[#0A2540]">{title}</h3>
    <div className="space-y-5">{children}</div>
  </section>
);

const CaseForm = () => {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();

  const [caseId, setCaseId] = useState(routeId === "new" ? null : routeId || null);
  const [caseDoc, setCaseDoc] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(routeId !== "new" && !!routeId);
  const [submitting, setSubmitting] = useState(false);
  const [savingNow, setSavingNow] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [errors, setErrors] = useState({});
  const [pageError, setPageError] = useState("");
  const [busy, setBusy] = useState(false);  // for lifecycle actions

  const dirtyRef = useRef(false);
  const hasInteractedRef = useRef(false);
  const ensuringRef = useRef(false);
  const debounceRef = useRef(null);
  const formRef = useRef(form);
  formRef.current = form;
  const caseIdRef = useRef(caseId);
  caseIdRef.current = caseId;

  // Permission flags
  const isOwner = caseDoc && user?.role === "agent" && caseDoc.agent_id === user.id;
  const isAssignedReviewer = caseDoc && user?.role === "reviewer" && caseDoc.reviewer_id === user.id;
  const isAdmin = user?.role === "admin";

  const canEdit = useMemo(() => {
    if (!caseDoc) return user?.role === "agent";  // /new flow
    const s = caseDoc.status;
    // Phase 14: agent owner can edit `draft` OR `pending_re_edit` (re-edit cycle).
    // Reviewer can edit only `under_review`. Admin is read-only on case body.
    if (isOwner) return s === "draft" || s === "pending_re_edit";
    if (isAssignedReviewer) return s === "under_review";
    return false;
  }, [caseDoc, isOwner, isAssignedReviewer, user]);

  const isReadOnly = !canEdit;
  const showSubmitButton =
    isOwner && (caseDoc?.status === "draft" || caseDoc?.status === "pending_re_edit");
  const submitButtonLabel =
    caseDoc?.status === "pending_re_edit" ? "resubmit_case_button" : "submit_case_button";

  // Hydrate form from case doc
  const hydrate = useCallback((data) => {
    setCaseDoc(data);
    setCaseId(data.id);
    setForm({
      applicant: { ...EMPTY_FORM.applicant, ...(data.applicant || {}) },
      spouse: { ...EMPTY_FORM.spouse, ...(data.spouse || {}) },
      health: null,  // Phase 11: not rendered any more; kept on schema only
      emergency_contact: {
        ...EMPTY_FORM.emergency_contact,
        ...(data.emergency_contact || {}),
      },
      files: {
        id_card: data.files?.id_card ?? null,
        sales_form: data.files?.sales_form ?? null,
        ctos_report: data.files?.ctos_report ?? null,
        photo: data.files?.photo ?? null,
        land_title: data.files?.land_title ?? null,
        customer_payment_slip: data.files?.customer_payment_slip ?? null,
        other_documents: data.files?.other_documents ?? [],
      },
    });
  }, []);

  const refresh = useCallback(async () => {
    if (!caseIdRef.current) return;
    try {
      const { data } = await api.get(`/cases/${caseIdRef.current}`);
      hydrate(data);
    } catch (err) {
      // ignore
    }
  }, [hydrate]);

  useEffect(() => {
    if (!routeId || routeId === "new") {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/cases/${routeId}`);
        hydrate(data);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404) {
          setPageError("Case not found");
        } else {
          setPageError(err?.response?.data?.detail || t("err_generic"));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [routeId, hydrate, t]);

  // Build payload (numeric coercion)
  const buildPayload = useCallback(() => {
    const f = formRef.current;
    const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    return {
      applicant: { ...f.applicant },
      spouse:
        f.applicant.marital_status === "married"
          ? {
              name: f.spouse?.name || "",
              contact: f.spouse?.contact || "",
              occupation: f.spouse?.occupation || "",
              company_name: f.spouse?.company_name || "",
              salary_per_month: num(f.spouse?.salary_per_month),
            }
          : null,
      // Phase 11: Health removed. Send null so backend stops persisting empty
      // values without breaking the optional schema field for legacy docs.
      health: null,
      emergency_contact: { ...f.emergency_contact },
    };
  }, []);

  const ensureCase = useCallback(async () => {
    if (caseIdRef.current) return caseIdRef.current;
    if (ensuringRef.current) {
      await new Promise((resolve) => {
        const t0 = setInterval(() => {
          if (caseIdRef.current) {
            clearInterval(t0);
            resolve();
          }
        }, 50);
      });
      return caseIdRef.current;
    }
    ensuringRef.current = true;
    try {
      const { data } = await api.post("/cases");
      setCaseId(data.id);
      caseIdRef.current = data.id;
      // Stub a temporary caseDoc until refresh
      setCaseDoc((c) => c || { id: data.id, status: "draft", agent_id: user?.id });
      window.history.replaceState(null, "", `/agent/cases/${data.id}/edit`);
      return data.id;
    } finally {
      ensuringRef.current = false;
    }
  }, [user]);

  const save = useCallback(async () => {
    if (isReadOnly && caseDoc) return;
    if (!hasInteractedRef.current) return;
    setSavingNow(true);
    try {
      const id = await ensureCase();
      const payload = buildPayload();
      await api.patch(`/cases/${id}?autosave=true`, payload);
      dirtyRef.current = false;
      setSavedAt(new Date());
    } catch (err) {
      // silent on autosave
    } finally {
      setSavingNow(false);
    }
  }, [buildPayload, ensureCase, isReadOnly, caseDoc]);

  // Debounced auto-save: 1s after last edit
  useEffect(() => {
    if (loading || isReadOnly) return;
    if (!dirtyRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      save();
    }, 1000);
    return () => clearTimeout(debounceRef.current);
  }, [form, loading, isReadOnly, save]);

  // Periodic safety net every 5s
  useEffect(() => {
    if (isReadOnly || loading) return;
    const i = setInterval(() => {
      if (dirtyRef.current && !savingNow) save();
    }, 5000);
    return () => clearInterval(i);
  }, [save, savingNow, isReadOnly, loading]);

  // Field updaters
  const setApplicant = (patch) => {
    if (isReadOnly) return;
    hasInteractedRef.current = true;
    dirtyRef.current = true;
    setForm((s) => ({ ...s, applicant: { ...s.applicant, ...patch } }));
  };
  const setSpouse = (patch) => {
    if (isReadOnly) return;
    hasInteractedRef.current = true;
    dirtyRef.current = true;
    setForm((s) => ({ ...s, spouse: { ...s.spouse, ...patch } }));
  };
  const setHealth = (patch) => {
    if (isReadOnly) return;
    hasInteractedRef.current = true;
    dirtyRef.current = true;
    setForm((s) => ({ ...s, health: { ...s.health, ...patch } }));
  };
  const setEmergency = (patch) => {
    if (isReadOnly) return;
    hasInteractedRef.current = true;
    dirtyRef.current = true;
    setForm((s) => ({ ...s, emergency_contact: { ...s.emergency_contact, ...patch } }));
  };
  const setFile = (slot, ref) => {
    setForm((s) => ({ ...s, files: { ...s.files, [slot]: ref } }));
  };
  const setOtherDocs = (items) => {
    setForm((s) => ({ ...s, files: { ...s.files, other_documents: items } }));
  };

  const handleSubmit = async () => {
    if (isReadOnly) return;
    setSubmitting(true);
    setErrors({});
    try {
      const id = await ensureCase();
      await api.patch(`/cases/${id}`, buildPayload());
      dirtyRef.current = false;
      await api.post(`/cases/${id}/submit`);
      toast.success(t("toast_case_submitted"));
      navigate("/agent");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 422 && Array.isArray(detail)) {
        const map = {};
        for (const e of detail) map[e.field] = e.message;
        setErrors(map);
        toast.error(t("case_form_invalid_submit"));
      } else {
        toast.error(detail || t("err_generic"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onSaveDraft = async () => {
    hasInteractedRef.current = true;
    dirtyRef.current = true;
    await save();
    toast.success(t("case_form_save_indicator_prefix") + formatTime(new Date()));
  };

  // ----- Lifecycle action handlers -----
  const wrapAction = async (fn, successKey) => {
    setBusy(true);
    try {
      await fn();
      if (successKey) toast.success(t(successKey));
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) toast.error(t("err_already_claimed"));
      else if (status === 422 && Array.isArray(detail)) {
        const map = {};
        for (const e of detail) map[e.field] = e.message;
        setErrors(map);
        toast.error(t("case_form_invalid_submit"));
      } else toast.error(detail || t("err_generic"));
    } finally {
      setBusy(false);
    }
  };

  const onClaim = () =>
    wrapAction(async () => {
      await api.post(`/cases/${caseId}/claim`);
      await refresh();
    }, "toast_claimed");

  const onRelease = () =>
    wrapAction(async () => {
      await api.post(`/cases/${caseId}/release`);
      await refresh();
      navigate("/reviewer");
    }, "toast_released");

  const onCorrection = null;  // Phase 9: removed
  const onResubmit = null;     // Phase 9: removed

  const onApprove = (message) =>
    wrapAction(async () => {
      await api.post(`/cases/${caseId}/approve`, { message });
      await refresh();
    }, "toast_approved");

  const onReject = (message) =>
    wrapAction(async () => {
      await api.post(`/cases/${caseId}/reject`, { message });
      await refresh();
    }, "toast_rejected");

  const onClose = () =>
    wrapAction(async () => {
      await api.post(`/cases/${caseId}/close`);
      await refresh();
    }, "toast_closed");

  // Phase 12 — Mark Paid handler (agent owner, status=approved)
  const onMarkPaid = () =>
    wrapAction(async () => {
      await api.post(`/cases/${caseId}/mark-paid`);
      await refresh();
    }, "toast_marked_paid");

  // Phase 14 — Re-submit for Edit (agent owner OR admin, from approved/paid/closed)
  const onResubmitForEdit = (reason) =>
    wrapAction(async () => {
      await api.post(`/cases/${caseId}/resubmit-for-edit`, { reason });
      await refresh();
    }, "toast_resubmitted_for_edit");

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <TopBar />
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-[#0A2540]" />
        </div>
      </div>
    );
  }

  if (pageError) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <TopBar />
        <main className="max-w-3xl mx-auto px-4 py-16 text-center">
          <div className="bg-white border border-slate-200 rounded-lg p-10 shadow-sm">
            <AlertCircle className="h-8 w-8 text-rose-500 mx-auto mb-3" />
            <p className="text-slate-700">{pageError}</p>
            <Button
              onClick={() => navigate(user?.role === "reviewer" ? "/reviewer" : "/agent")}
              variant="outline"
              className="mt-6"
              data-testid="case-form-back-error"
            >
              {t("case_form_view_back")}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const isMarried = form.applicant.marital_status === "married";
  const title = caseId ? t("case_form_edit_title") : t("case_form_new_title");
  const backTo = user?.role === "reviewer" ? "/reviewer" : user?.role === "admin" ? "/admin" : "/agent";

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="case-form">
      <TopBar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <button
              type="button"
              onClick={() => navigate(backTo)}
              className="inline-flex items-center text-sm text-slate-500 hover:text-[#0A2540] mb-2 focus-ring rounded px-1"
              data-testid="case-form-back"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t("nav_back")}
            </button>
            <h2 className="font-display text-3xl tracking-tight font-medium text-[#0A2540] flex items-center gap-3">
              {caseDoc?.case_number ? (
                <span
                  className="inline-flex items-center px-3 py-1 rounded-full text-base font-mono font-medium bg-[#0A2540] text-white shrink-0"
                  data-testid="case-form-number-chip"
                >
                  {t("case_number_short")} {formatCaseNumber(caseDoc.case_number)}
                </span>
              ) : null}
              <span className="truncate">{title}</span>
            </h2>
          </div>
          {caseDoc?.id ? (
            <div className="flex flex-col items-stretch sm:items-end gap-2">
              <CaseExportButtons
                caseId={caseDoc.id}
                caseStatus={caseDoc.status}
                caseNumber={caseDoc.case_number}
                applicantName={(caseDoc.applicant || {}).name}
              />
              {!isReadOnly ? (
                <div className="flex items-center gap-3 text-sm" data-testid="autosave-indicator">
                  {savingNow ? (
                    <span className="inline-flex items-center text-slate-500">
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      {t("case_form_saving")}
                    </span>
                  ) : savedAt ? (
                    <span className="inline-flex items-center text-emerald-700">
                      <CheckCircle2 className="h-4 w-4 mr-1.5" />
                      {t("case_form_save_indicator_prefix")}
                      {formatTime(savedAt)}
                    </span>
                  ) : (
                    <span className="text-slate-400">{t("case_form_unsaved")}</span>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Lifecycle banner (only when we have a real caseDoc) */}
        {caseDoc && caseDoc.status ? (
          <LifecycleBanner
            caseDoc={caseDoc}
            user={user}
            busy={busy}
            onClaim={onClaim}
            onRelease={onRelease}
            onApprove={onApprove}
            onReject={onReject}
            onClose={onClose}
            onMarkPaid={onMarkPaid}
            onResubmitForEdit={onResubmitForEdit}
          />
        ) : null}

        {/* Phase 12 — Customer Payment Section (visible from `approved` onward) */}
        {caseDoc && ["approved", "paid", "closed"].includes(caseDoc.status) ? (
          <Section title={t("payment_section_title")} testId="section-payment">
            <FileSlot
              slot="customer_payment_slip"
              en="Customer Payment Slip"
              zh="客户付款凭证"
              required
              caseId={caseId}
              ensureCase={null}
              fileRef={(caseDoc.files || {}).customer_payment_slip}
              onChange={async () => { await refresh(); }}
              disabled={!(isOwner && caseDoc.status === "approved")}
            />
            <p className="text-xs text-slate-500" data-testid="payment-section-help">
              {caseDoc.status === "approved"
                ? (isOwner ? t("payment_customer_slip_help_agent") : t("payment_customer_slip_help_others"))
                : caseDoc.status === "paid"
                ? (isAdmin ? t("payment_paid_admin") : t("payment_paid_other"))
                : t("payment_done")}
            </p>
          </Section>
        ) : null}

        {/* Applicant */}
        <Section title={t("case_form_section_applicant")} testId="section-applicant">
          <div className="grid gap-5 md:grid-cols-2">
            <BiField id="ap-name" en="Name" zh="名字" required error={errorFor(errors, "applicant.name")}>
              <Input
                id="ap-name"
                value={form.applicant.name}
                onChange={(e) => setApplicant({ name: e.target.value })}
                disabled={isReadOnly}
                data-testid="applicant-name"
              />
            </BiField>
            <BiField id="ap-chinese-name" en="Chinese Name" zh="中文名字" error={errorFor(errors, "applicant.chinese_name")}>
              <Input
                id="ap-chinese-name"
                value={form.applicant.chinese_name || ""}
                onChange={(e) => setApplicant({ chinese_name: e.target.value })}
                disabled={isReadOnly}
                data-testid="applicant-chinese-name"
                maxLength={200}
              />
            </BiField>
            <BiField id="ap-tel" en="Tel" zh="电话号码" required error={errorFor(errors, "applicant.tel")}>
              <Input
                id="ap-tel"
                value={form.applicant.tel}
                onChange={(e) => setApplicant({ tel: e.target.value })}
                disabled={isReadOnly}
                data-testid="applicant-tel"
              />
            </BiField>
            <BiField id="ap-email" en="Email Address" zh="电邮地址" required error={errorFor(errors, "applicant.email")}>
              <Input
                id="ap-email"
                type="email"
                value={form.applicant.email}
                onChange={(e) => setApplicant({ email: e.target.value })}
                disabled={isReadOnly}
                data-testid="applicant-email"
              />
            </BiField>
            <BiField id="ap-mother" en="Mother Name" zh="母亲名字" required error={errorFor(errors, "applicant.mother_name")}>
              <Input
                id="ap-mother"
                value={form.applicant.mother_name}
                onChange={(e) => setApplicant({ mother_name: e.target.value })}
                disabled={isReadOnly}
                data-testid="applicant-mother-name"
              />
            </BiField>
          </div>
          <BiField
            id="ap-mailing"
            en="Mailing Address"
            zh="书信地址"
            required
            error={errorFor(errors, "applicant.mailing_address")}
          >
            <Textarea
              id="ap-mailing"
              rows={2}
              value={form.applicant.mailing_address}
              onChange={(e) => setApplicant({ mailing_address: e.target.value })}
              disabled={isReadOnly}
              data-testid="applicant-mailing-address"
            />
          </BiField>
        </Section>

        {/* Marital */}
        <Section title={t("case_form_section_marital")} testId="section-marital">
          <BiField
            en="Single or Married"
            zh="单身或已婚"
            required
            error={errorFor(errors, "applicant.marital_status")}
          >
            <div className="flex gap-3" data-testid="marital-status-group">
              {[
                { v: "single", l: t("case_form_marital_single") },
                { v: "married", l: t("case_form_marital_married") },
              ].map((opt) => {
                const active = form.applicant.marital_status === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setApplicant({ marital_status: opt.v })}
                    disabled={isReadOnly}
                    className={`flex-1 px-4 py-3 rounded-md border text-sm font-medium transition-colors focus-ring ${
                      active
                        ? "bg-[#0A2540] text-white border-[#0A2540]"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                    } ${isReadOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                    data-testid={`marital-${opt.v}`}
                  >
                    {opt.l}
                  </button>
                );
              })}
            </div>
          </BiField>
        </Section>

        {/* Spouse (conditional) */}
        {isMarried ? (
          <Section title={t("case_form_section_spouse")} testId="section-spouse">
            <div className="grid gap-5 md:grid-cols-2">
              <BiField id="sp-name" en="Spouse Name" zh="配偶名字" required error={errorFor(errors, "spouse.name")}>
                <Input id="sp-name" value={form.spouse.name} onChange={(e) => setSpouse({ name: e.target.value })} disabled={isReadOnly} data-testid="spouse-name" />
              </BiField>
              <BiField id="sp-contact" en="Spouse Contact" zh="配偶号码" required error={errorFor(errors, "spouse.contact")}>
                <Input id="sp-contact" value={form.spouse.contact} onChange={(e) => setSpouse({ contact: e.target.value })} disabled={isReadOnly} data-testid="spouse-contact" />
              </BiField>
              <BiField id="sp-occupation" en="Spouse Occupation" zh="配偶职业" required error={errorFor(errors, "spouse.occupation")}>
                <Input id="sp-occupation" value={form.spouse.occupation} onChange={(e) => setSpouse({ occupation: e.target.value })} disabled={isReadOnly} data-testid="spouse-occupation" />
              </BiField>
              <BiField id="sp-company" en="Spouse Company Name" zh="配偶公司名字" required error={errorFor(errors, "spouse.company_name")}>
                <Input id="sp-company" value={form.spouse.company_name} onChange={(e) => setSpouse({ company_name: e.target.value })} disabled={isReadOnly} data-testid="spouse-company-name" />
              </BiField>
              <BiField id="sp-salary" en="Spouse Salary Per Month (RM)" zh="配偶月入" required error={errorFor(errors, "spouse.salary_per_month")}>
                <Input id="sp-salary" type="number" step="0.01" min="0" value={form.spouse.salary_per_month ?? ""} onChange={(e) => setSpouse({ salary_per_month: e.target.value })} disabled={isReadOnly} data-testid="spouse-salary" />
              </BiField>
            </div>
          </Section>
        ) : null}

        {/* Phase 11 — Health section removed entirely */}

        {/* Emergency contact */}
        <Section title={t("case_form_section_emergency")} testId="section-emergency">
          <div className="grid gap-5 md:grid-cols-2">
            <BiField id="em-name" en="Name" zh="名字" required error={errorFor(errors, "emergency_contact.name") || errorFor(errors, "emergency_contact")}>
              <Input id="em-name" value={form.emergency_contact.name} onChange={(e) => setEmergency({ name: e.target.value })} disabled={isReadOnly} data-testid="emergency-name" />
            </BiField>
            <BiField id="em-tel" en="Tel" zh="电话号码" required error={errorFor(errors, "emergency_contact.tel")}>
              <Input id="em-tel" value={form.emergency_contact.tel} onChange={(e) => setEmergency({ tel: e.target.value })} disabled={isReadOnly} data-testid="emergency-tel" />
            </BiField>
            <BiField id="em-relationship" en="Relationship" zh="关系" required error={errorFor(errors, "emergency_contact.relationship")}>
              <Input id="em-relationship" value={form.emergency_contact.relationship} onChange={(e) => setEmergency({ relationship: e.target.value })} disabled={isReadOnly} data-testid="emergency-relationship" />
            </BiField>
          </div>
          <BiField id="em-address" en="Resident Address" zh="住家地址" required error={errorFor(errors, "emergency_contact.resident_address")}>
            <Textarea id="em-address" rows={2} value={form.emergency_contact.resident_address} onChange={(e) => setEmergency({ resident_address: e.target.value })} disabled={isReadOnly} data-testid="emergency-address" />
          </BiField>
        </Section>

        {/* Documents */}
        <Section title={t("case_form_section_files")} testId="section-files">
          <div className="grid gap-5 md:grid-cols-2">
            <FileSlot slot="id_card" en="Identification Card" zh="身份证" required caseId={caseId} ensureCase={ensureCase} fileRef={form.files.id_card} onChange={(ref) => setFile("id_card", ref)} disabled={isReadOnly} error={errorFor(errors, "files.id_card")} />
            <FileSlot slot="sales_form" en="Sales Form" zh="销售表格" required caseId={caseId} ensureCase={ensureCase} fileRef={form.files.sales_form} onChange={(ref) => setFile("sales_form", ref)} disabled={isReadOnly} error={errorFor(errors, "files.sales_form")} />
            <FileSlot slot="ctos_report" en="CTOS Report" zh="CTOS 报告" caseId={caseId} ensureCase={ensureCase} fileRef={form.files.ctos_report} onChange={(ref) => setFile("ctos_report", ref)} disabled={isReadOnly} />
            <FileSlot slot="photo" en="Photo" zh="照片" caseId={caseId} ensureCase={ensureCase} fileRef={form.files.photo} onChange={(ref) => setFile("photo", ref)} disabled={isReadOnly} />
            <FileSlot slot="land_title" en="Land Title" zh="地契" caseId={caseId} ensureCase={ensureCase} fileRef={form.files.land_title} onChange={(ref) => setFile("land_title", ref)} disabled={isReadOnly} />
          </div>
          <OtherDocuments caseId={caseId} ensureCase={ensureCase} items={form.files.other_documents} onChange={setOtherDocs} disabled={isReadOnly} />
        </Section>

        {/* Status thread (only when caseId exists) */}
        {caseId && caseDoc ? (
          <StatusThread caseId={caseId} caseStatus={caseDoc.status} onActivity={refresh} />
        ) : null}

        {/* Save Draft / Submit footer (only for editable contexts) */}
        {!isReadOnly ? (
          <div className="sticky bottom-0 z-30 bg-gradient-to-t from-[#F8FAFC] via-[#F8FAFC] to-transparent pt-6 pb-4">
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-sm text-slate-500">
                {savingNow
                  ? t("case_form_saving")
                  : savedAt
                  ? `${t("case_form_save_indicator_prefix")}${formatTime(savedAt)}`
                  : t("case_form_unsaved")}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onSaveDraft} disabled={savingNow} data-testid="save-draft-button">
                  <Save className="h-4 w-4 mr-2" />
                  {t("case_form_save_draft")}
                </Button>
                {showSubmitButton ? (
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="bg-[#0A2540] hover:bg-[#051626] text-white"
                    data-testid="submit-case-button"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t("case_form_submitting")}
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        {t(caseDoc?.status === "pending_re_edit" ? "case_form_resubmit" : "case_form_submit")}
                      </>
                    )}
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default CaseForm;
